import React, { useState, useRef, useEffect } from 'react'
import styled from '@emotion/styled'
import Button from '@components/common/form/Button'
import type { ChatMessage } from './useMenuGeneration'

interface MenuGenerationChatProps {
  messages: ChatMessage[]
  isLoading: boolean
  loadingStatus?: string
  error: string | null
  onSendMessage: (message: string) => void
  onStopGeneration: () => void
}

const MARKDOWN_LINK_REGEX = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g
const URL_REGEX = /(https?:\/\/[^\s<>)"\]]+)/g

const linkifyBareUrls = (text: string): React.ReactNode => {
  const parts = text.split(URL_REGEX)
  if (parts.length === 1) {
    return text
  }

  return parts.map((part, i) =>
    URL_REGEX.test(part) ? (
      <a key={i} href={part} target="_blank" rel="noopener noreferrer">
        {part}
      </a>
    ) : (
      part
    ),
  )
}

const linkifyText = (text: string): React.ReactNode => {
  // First pass: split on markdown links [text](url)
  const mdParts = text.split(MARKDOWN_LINK_REGEX)
  if (mdParts.length === 1) {
    return linkifyBareUrls(text)
  }

  // split with 2 capture groups produces: [before, linkText, url, between, ...]
  const result: React.ReactNode[] = []
  for (let i = 0; i < mdParts.length; i++) {
    if (i % 3 === 0) {
      result.push(
        <React.Fragment key={i}>{linkifyBareUrls(mdParts[i])}</React.Fragment>,
      )
    } else if (i % 3 === 1) {
      const linkText = mdParts[i]
      const href = mdParts[i + 1]
      result.push(
        <a key={i} href={href} target="_blank" rel="noopener noreferrer">
          {linkText}
        </a>,
      )
    }
  }

  return result
}

const MenuGenerationChat: React.FC<MenuGenerationChatProps> = ({
  messages,
  isLoading,
  loadingStatus = 'Thinking...',
  error,
  onSendMessage,
  onStopGeneration,
}) => {
  const [input, setInput] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isLoading])

  const handleSend = () => {
    if (isLoading) {
      return
    }
    const trimmed = input.trim()
    if (!trimmed) {
      return
    }
    onSendMessage(trimmed)
    setInput('')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <ChatContainer>
      <MessagesArea>
        {messages.length === 0 && (
          <EmptyState>
            Start by asking to generate a schedule. For example:
            <br />
            <em>{`"Create menus for June"`}</em>
          </EmptyState>
        )}
        {messages.map((msg, i) => (
          <MessageBubble key={i} role={msg.role}>
            <MessageRole>
              {msg.role === 'user' ? 'You' : 'Assistant'}
            </MessageRole>
            <MessageText>{linkifyText(msg.text)}</MessageText>
          </MessageBubble>
        ))}
        {isLoading && (
          <MessageBubble role="assistant">
            <MessageRole>Assistant</MessageRole>
            <LoadingDots>{loadingStatus}</LoadingDots>
          </MessageBubble>
        )}
        {error && <ErrorMessage>{error}</ErrorMessage>}
        <div ref={messagesEndRef} />
      </MessagesArea>
      <InputArea>
        <ChatInput
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message..."
        />
        {isLoading && <StopButton onClick={onStopGeneration}>Stop</StopButton>}
        <Button
          label="Send"
          onClick={handleSend}
          disabled={isLoading || !input.trim()}
        />
      </InputArea>
    </ChatContainer>
  )
}

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 400px;
`

const MessagesArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const EmptyState = styled.div`
  color: #888;
  text-align: center;
  padding: 40px 20px;
  font-size: 14px;
  line-height: 1.6;
`

const MessageBubble = styled.div<{ role: string }>`
  padding: 10px 14px;
  border-radius: 8px;
  max-width: 85%;
  align-self: ${(props) => (props.role === 'user' ? 'flex-end' : 'flex-start')};
  background: ${(props) => (props.role === 'user' ? '#e3f2fd' : '#f5f5f5')};
`

const MessageRole = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #666;
  margin-bottom: 4px;
  text-transform: uppercase;
`

const MessageText = styled.div`
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;

  a {
    color: #1a73e8;
    text-decoration: underline;
  }
`

const LoadingDots = styled.div`
  color: #888;
  font-style: italic;
`

const ErrorMessage = styled.div`
  color: #d32f2f;
  background: #ffebee;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 13px;
`

const InputArea = styled.div`
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #e0e0e0;
  align-items: flex-end;
`

const StopButton = styled.button`
  padding: 8px 16px;
  border: 1px solid #d32f2f;
  border-radius: 4px;
  background: white;
  color: #d32f2f;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: #ffebee;
  }
`

const ChatInput = styled.textarea`
  flex: 1;
  padding: 10px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  resize: none;
  min-height: 40px;
  max-height: 120px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #1a73e8;
  }

  &:disabled {
    background: #f5f5f5;
  }
`

export default MenuGenerationChat
